// Tiempo real de la tienda (decisión D53): el mismo Socket.IO del panel (`/rt`), con la sesión de la tienda. El
// servidor solo la deja en la sala de sus negocios: llegan sus mensajes con la distribuidora, las tildes, "escribiendo…"
// y los cambios de sus pedidos; nunca nada del personal. La tienda no depende del socket: sin él, las pantallas se
// ponen al día al abrirse.
// - Va por el mismo origen (`/socket.io`): en producción Caddy lo pasa a la API; en desarrollo, el proxy de Vite.
// - El token dura 30 minutos y el servidor corta al vencer: se refresca la sesión y se vuelve a conectar con el nuevo.
import { io, type Socket } from 'socket.io-client';
import { refrescarSesion } from './api';
import { useSesion } from './sesion';

type Oyente = (datos: unknown) => void;

let socket: Socket | null = null;
let tokenActual: string | null = null;
const oyentes = new Map<string, Set<Oyente>>();

/** Se anota a un evento del servidor; sobrevive a reconexiones. Devuelve cómo desanotarse. */
export function escuchar(evento: string, f: Oyente): () => void {
  let lista = oyentes.get(evento);
  if (!lista) oyentes.set(evento, (lista = new Set()));
  lista.add(f);
  return () => {
    lista.delete(f);
  };
}

/** Aviso liviano al servidor ("escribiendo…"). Sin conexión, se pierde (no importa). */
export function emitir(evento: string, datos: unknown): void {
  if (socket?.connected) socket.emit(evento, datos);
}

export const conectado = (): boolean => !!socket?.connected;

// A la vista o no: con la tienda a la vista, el servidor no manda push (decisión D40).
const avisarVisible = () => socket?.emit('visible', { visible: document.visibilityState === 'visible' });

function conectar(token: string) {
  desconectar();
  tokenActual = token;
  const s = io('/rt', {
    path: '/socket.io',
    // Se lee en cada intento: al reconectar va el token vigente.
    auth: (cb) => cb({ token: useSesion.getState().sesion?.access_token ?? '' }),
    transports: ['websocket', 'polling'],
    reconnectionDelayMax: 30_000,
  });
  socket = s;
  s.onAny((evento: string, datos: unknown) => oyentes.get(evento)?.forEach((f) => f(datos)));
  s.on('listo', () => {
    avisarVisible();
    oyentes.get('conectado')?.forEach((f) => f(undefined));
  });
  // El servidor corta al vencer el token (o si la sesión se cerró): se renueva y se vuelve a conectar.
  let intentos = 0;
  const reintentar = () => {
    if (++intentos > 4) return;
    setTimeout(async () => {
      if (socket !== s) return;
      const usado = useSesion.getState().sesion?.access_token ?? null;
      const ok = await refrescarSesion(usado).catch(() => false);
      if (ok && socket === s) s.connect();
    }, 1_500 * intentos);
  };
  s.on('error_auth', reintentar);
  s.on('disconnect', (motivo) => {
    if (motivo === 'io server disconnect') reintentar();
  });
  s.on('connect', () => (intentos = 0));
}

function desconectar() {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  tokenActual = null;
}

let iniciado = false;

/** Se llama una vez al arrancar: con sesión se conecta y sin sesión se desconecta (al entrar o salir). */
export function iniciarTiempoReal(): void {
  if (iniciado || typeof window === 'undefined') return;
  iniciado = true;
  const revisar = () => {
    const s = useSesion.getState().sesion;
    // El preventista (decisión D60) no tiene mensajes ni avisos de la tienda: es la sesión del personal.
    if (!s || s.vendedor) return desconectar();
    // Otra persona en el mismo navegador (salió y entró otro número): conexión nueva.
    if (!socket || (tokenActual !== s.access_token && !socket.connected)) conectar(s.access_token);
  };
  useSesion.subscribe(revisar);
  revisar();
  document.addEventListener('visibilitychange', avisarVisible);
}
